import React, { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  Grid,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';

import {
  BankAccount,
  BankAccountDutch,
  Page,
  StyledButton,
  StyledIconButton,
  StyledSelect,
  StyledSwitch,
  StyledTextField,
  Typography,
  UserSearch
} from '../../components/interface';

import { AlertSaved } from '../../components/alerts/Saved';
import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import { DividendCalculation } from '../../models/DividendCalculation';
import { Exit } from '../../models/Exit';
import { PainRow } from '../../models/PainRow';
import { Share } from '../../models/Share';
import { User } from '../../models/User';

import { fetch_one } from '../../lib/server_helper';
import { fetchAll } from '../../lib/v31lib';
import { loginAtom } from '../../lib/auth';

import { PAIN_ACCOUNTS } from './accounts';

const { DateTime } = require("luxon");

type RowNewProps = {};

type PainEntry = {
  name: string;
  amount: string;
  iban: string;
  iban_ok: boolean;
  country: string;
  note: string;
  save_iban_to_user: boolean;
  dividend_calculation_id?: number;
  last_ibans?: LastIbans;
};

type LastIbans = {BE?: string; NL?: string};

type ExitInfo = {
  given_id: number;
  label: string;
  amount: number;
};

type DividendInfo = {
  id: number;
  book_year: string;
  amount?: number;
  reference: string;
  locale: string;
  in_pain_file: boolean;
};

const emptyEntry = (): PainEntry => ({name: '', amount: '', iban: '', iban_ok: false, country: 'BE', note: '', save_iban_to_user: false});

const userEntry = (u?: User): PainEntry => !u ? emptyEntry() : {
  ...emptyEntry(),
  name: u.full_name || '',
  iban: u.zicht_account || '',
  iban_ok: !!u.zicht_account,
  country: !!u.is_dutch ? 'NL' : 'BE'
};

const parseAmount = (raw: string): number => {
  let s = raw.replace(/\s/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  return parseFloat(s);
};

const formatEur = (v: number): string => v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const normalizeIban = (iban?: string): string => (iban || '').replace(/\s/g, '').toUpperCase();

const ibanValid = (iban: string): boolean => {
  const clean = normalizeIban(iban).replace(/[^A-Z0-9]/g, '');
  const code = clean.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
  if (!code) return false;
  const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter) => (letter.charCodeAt(0) - 55).toString());
  let checksum = digits.slice(0, 2), fragment;
  for (let offset = 2; offset < digits.length; offset += 7) {
    fragment = String(checksum) + digits.substring(offset, offset + 7);
    checksum = (parseInt(fragment, 10) % 97).toString();
  }
  return checksum === '1';
};

const exitPayoutAmount = (exit: Exit, shares: Share[], vennoot_number?: string): number | undefined => {
  const sell_amount = parseInt(exit.extra_data?.sell_amount || '0', 10);
  if (!sell_amount) return undefined;

  let share_amount: number | undefined = exit.user_share_type_amount;
  if (!!exit.extra_data?.date && DateTime.fromISO(exit.extra_data.date) < DateTime.fromISO('2020-08-28')) {
    const tag = (vennoot_number || '').startsWith('B') ? 'B' : 'A';
    share_amount = shares.filter((s) => s.tag === tag).map((s) => parseFloat(s.amount)).find((v) => !isNaN(v));
  }
  if (!share_amount) return undefined;

  return sell_amount * share_amount;
};

const EXIT_REFS: Record<string, string> = {
  nl: '[{{vennr}}] Terugbetaling van Argen-Co aandelen',
  fr: "[{{vennr}}] Remboursement actions d'Argen-Co"
};

const exitRef = (locale: string, vennoot_number?: string): string => {
  return EXIT_REFS[locale].replace('{{vennr}}', vennoot_number || '');
};

const DIVIDEND_REFS: Record<string, string> = {
  nl: 'Argen-Co dividend {{book_year}} - referentie {{vennr}} - {{reference}}',
  fr: "Argen-Co dividende {{book_year}} - référence {{vennr}} - {{reference}}"
};

const dividendRef = (locale: string, book_year: string, vennr: string, reference: string): string => {
  return (DIVIDEND_REFS[locale] || DIVIDEND_REFS.nl)
    .replace('{{book_year}}', book_year)
    .replace('{{vennr}}', vennr)
    .replace('{{reference}}', reference);
};

// vennr-agnostic: matches the note template regardless of which vennr was filled in
const looksLikeExitRef = (note: string): boolean => {
  const pattern = /^\[[^\]]+\] (Terugbetaling van Argen-Co aandelen|Remboursement actions d'Argen-Co)$/;
  return pattern.test(note);
};

// values-agnostic: matches the dividend note template regardless of which book year/vennr/reference were filled in
const DIVIDEND_REF_PATTERN = /^Argen-Co divide(?:nd|nde) (.+) - (?:referentie|référence) (.+) - (.+)$/;

const switchDividendRefLocale = (note: string, locale: string): string => {
  const parts = note.match(DIVIDEND_REF_PATTERN);
  if (!parts) return note;
  return dividendRef(locale, parts[1], parts[2], parts[3]);
};

export const PainRowNew: FunctionComponent<RowNewProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();
  const params = useParams();

  const editId = params.id;

  const [login, setLogin] = useAtom(loginAtom);

  const [accountType, setAccountType] = useState<string>(!!state?.account_type ? state.account_type : '');  const [entries, setEntries] = useState<PainEntry[]>([!!state?.user ? {
    ...emptyEntry(),
    name: state.user.full_name || '',
    iban: state.user.zicht_account || '',
    iban_ok: !!state.user.zicht_account,
    country: !!state.user.is_dutch ? 'NL' : 'BE'
  } : emptyEntry()]);
  const [user, setUser] = useState<User | undefined>(!!state?.user ? state.user : undefined);
  const [showSearch, setShowSearch] = useState<boolean>(!!!state?.user && !!!editId);
  const [exitInfo, setExitInfo] = useState<ExitInfo>();
  const [exitLoading, setExitLoading] = useState<boolean>(false);
  const [dividends, setDividends] = useState<DividendInfo[]>([]);
  const [dividendLoading, setDividendLoading] = useState<boolean>(false);
  const [generalNote, setGeneralNote] = useState<string>('');
  const [noteLocale, setNoteLocale] = useState<string>('nl');
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedSnackOpen, setSavedSnackOpen] = useState<boolean>(false);
  const [nameCopiedSnackOpen, setNameCopiedSnackOpen] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);
  // BankAccount components keep internal state, so entries need a remount to display fresh IBAN values
  const [entryEpoch, setEntryEpoch] = useState<number>(0);

  useEffect(() => {
    if (!editId || !login) return;
    let active = true;

    fetchAll<PainRow>('/nl/v3/objects/fetch_all', {
      object: 'pain_row',
      fields: ['id', 'account_type', 'amount', 'name', 'iban', 'note', 'user_id', 'user|full_name', 'user|vennoot_number', 'user|zicht_account', 'user|is_dutch'],
      id: editId
    }, login).then((result) => {
      if (!active || !result) return;
      const row = Array.isArray(result) ? result[0] : result;
      if (!row) return;
      setAccountType(row.account_type || '');
      setEntries([{
        name: row.name || '',
        amount: row.amount !== undefined ? parseFloat(String(row.amount)).toFixed(2) : '',
        iban: row.iban || '',
        iban_ok: true,
        country: (row.iban || '').startsWith('NL') ? 'NL' : 'BE',
        note: row.note || '',
        save_iban_to_user: false
      }]);
      if (!!row.user_id && !!row.user_full_name) {
        setUser({
          id: row.user_id,
          full_name: row.user_full_name,
          vennoot_number: row.user_vennoot_number,
          zicht_account: row.user_zicht_account,
          is_dutch: row.user_is_dutch
        } as User);
      }
    }).catch(() => {});

    return () => {active = false;};
  }, [editId, login]);

  useEffect(() => {
    setExitInfo(undefined);
    if (!user || accountType !== 'exit' || !!!user.vennoot_number || !!editId) {
      setExitLoading(false);
      return;
    }

    if (entries.length === 0) {
      setEntries([userEntry(user)]);
    } else {
      setEntries(entries.map((e) => {
        if (!looksLikeExitRef(e.note) && e.note !== '') return e;
        return {...e, note: exitRef(noteLocale, user.vennoot_number)};
      }));
    }

    setExitLoading(true);
    let active = true;
    Promise.all([
      fetchAll<Exit>('/nl/v3/objects/fetch_all', {
        object: 'exit',
        fields: ['id', 'given_id', 'updated_at', 'cancelled', 'extra_data', 'book_year|visual', 'user|vennoot_number', 'user|share_type|amount'],
        per_page: 20,
        page: 0,
        order: 'updated_at DESC',
        filter: {search: user.vennoot_number}
      }, login),
      fetchAll<Share>('/nl/v3/objects/fetch_all', {
        object: 'share',
        fields: ['id', 'tag', 'amount'],
        per_page: 100,
        page: 0
      }, login)
    ]).then(([exits, shares]) => {
      if (!active) return;
      setExitLoading(false);
      const match = exits.find((e) => e.user_vennoot_number === user.vennoot_number && !e.cancelled && (e.extra_data?.checked || '').toString() === '1');
      if (!match) return;
      const amount = exitPayoutAmount(match, shares, user.vennoot_number);
      if (!amount) return;
      setExitInfo({
        given_id: match.given_id,
        label: !!match.book_year_visual ? match.book_year_visual : 'death',
        amount: amount
      });
    }).catch(() => {if (active) setExitLoading(false);});

    return () => {active = false;};
  }, [user, accountType]);

  useEffect(() => {
    setDividends([]);
    if (!user || accountType !== 'dividend' || !!!user.vennoot_number || !!editId) {
      setDividendLoading(false);
      if (!editId && entries.length === 0) setEntries([userEntry(user)]);
      return;
    }

    setEntries([]);
    setDividendLoading(true);
    let active = true;
    Promise.all([
      fetchAll<DividendCalculation>('/nl/v3/objects/fetch_all', {
        object: 'dividend_calculation',
        fields: ['id', 'extra_data', 'dividend|book_year|short_visual', 'user|vennoot_number', 'user|locale'],
        per_page: 20,
        page: 0,
        order: 'created_at DESC',
        filter: {search: user.vennoot_number}
      }, login),
      fetchAll<User>('/nl/v3/objects/fetch_all', {
        object: 'user',
        fields: ['id', 'extra_data'],
        id: user.id
      }, login)
    ]).then(([calcs, user_rows]) => {
      if (!active) return;
      setDividendLoading(false);
      const raw_rows: User | User[] = Array.isArray(user_rows) ? user_rows : (user_rows as unknown as User);
      const user_row: User | undefined = Array.isArray(raw_rows) ? raw_rows[0] : raw_rows;
      const pain_notes = user_row?.extra_data?.linked_pains || [];
      const seen_notes: string[] = pain_notes.map((pn) => pn.line?.note || '');
      const list = calcs.filter((c) => c.user_vennoot_number === user.vennoot_number).slice(0, 5).map((c) => {
        const payment = c.extra_data?.calculation?.bottomline?.actual_payment;
        const reference = c.extra_data?.calculation?.bottomline?.reference || '';
        return {
          id: c.id || 0,
          book_year: c.dividend_book_year_short_visual || '',
          amount: payment !== undefined && !isNaN(Number(payment)) ? Number(payment) : undefined,
          reference: reference,
          locale: c.user_locale || 'nl',
          in_pain_file: reference !== '' && seen_notes.some((n) => n.includes(reference))
        };
      });
      setDividends(list);
      if (list.length === 0) setEntries([userEntry(user)]);
    }).catch(() => {
      if (!active) return;
      setDividendLoading(false);
      setEntries([userEntry(user)]);
    });

    return () => {active = false;};
  }, [user, accountType]);

  const updateEntry = (index: number, patch: Partial<PainEntry>) => {
    setEntries(entries.map((e, i) => i === index ? {...e, ...patch} : e));
  };

  const switchCountry = (index: number, country: 'BE' | 'NL') => {
    const entry = entries[index];
    if (entry.country === country) return;
    const stashed: LastIbans = {...(entry.last_ibans || {}), [entry.country]: entry.iban};
    const on_file = normalizeIban(user?.zicht_account);
    const restore = stashed[country] || (on_file.startsWith(country) ? on_file : '');
    updateEntry(index, {
      country,
      iban: restore,
      iban_ok: restore !== '' && ibanValid(restore),
      last_ibans: stashed
    });
  };

  const addDividendEntry = (d: DividendInfo) => {
    setErrors([]);
    setEntries([...entries, {
      ...emptyEntry(),
      name: user?.full_name || '',
      amount: d.amount !== undefined ? d.amount.toFixed(2) : '',
      iban: user?.zicht_account || '',
      iban_ok: !!user?.zicht_account,
      country: !!user?.is_dutch ? 'NL' : 'BE',
      note: dividendRef(d.locale, d.book_year, user?.vennoot_number || '', d.reference),
      dividend_calculation_id: d.id
    }]);
  };

  const setEntryLocale = (index: number, locale: string) => {
    setNoteLocale(locale);
    setEntries(entries.map((e, i) => {
      if (i !== index) return e;
      if (looksLikeExitRef(e.note)) return {...e, note: exitRef(locale, user?.vennoot_number)};
      if (DIVIDEND_REF_PATTERN.test(e.note)) return {...e, note: switchDividendRefLocale(e.note, locale)};
      return e;
    }));
  };

  const prefillNote = (index: number) => {
    setEntries(entries.map((e, i) => {
      if (i !== index) return e;
      if (!looksLikeExitRef(e.note)) return e;
      return {...e, note: exitRef(noteLocale, user?.vennoot_number)};
    }));
  };

  const pickUser = (u: User) => {
    setUser(u);
    setShowSearch(false);
    setEntryEpoch(entryEpoch + 1);
    setEntries(entries.map((e, i) => i === 0 ? {
      ...e,
      name: u.full_name || `${u.first_name} ${u.last_name}`,
      iban: u.zicht_account || '',
      iban_ok: !!u.zicht_account,
      country: !!u.is_dutch ? 'NL' : 'BE'
    } : e));
  };

  const totalAmount = () => entries.map((e) => parseAmount(e.amount)).filter((v) => !isNaN(v)).reduce((a, b) => a + b, 0);

  const totalsMatch = () => !!exitInfo && Math.abs(totalAmount() - exitInfo.amount) < 0.005;

  const entriesValid = () => {
    if (accountType === '') return false;
    if (entries.length === 0) return false;
    return entries.every((e) => e.name.trim() !== '' && e.iban_ok && e.iban !== '' && !isNaN(parseAmount(e.amount)) && parseAmount(e.amount) > 0 && e.note.trim() !== '');
  };

  const canSave = () => !saving && entriesValid() && (!exitInfo || totalsMatch());

  const finish = () => {
    setSavedSnackOpen(true);
    setTimeout(() => {navigate('/create_pain');}, 500);
  };

  const saveNote = () => {
    if (!user || generalNote.trim() === '') {finish(); return;}

    let formData = new FormData();
    formData.append('object', 'user_note');
    formData.append('ob[comment]', generalNote);
    formData.append('ob[user_id]', (user.id || '').toString());
    formData.append('ob[admin_id]', (login?.id || '').toString());

    fetch_one('/nl/v3/objects/save', formData, (ob, complete_data) => {
      finish();
    }, (z) => {
      finish();
    }, login);
  };

  const doSave = () => {
    let new_errors:string[] = [];
    if (accountType === '') new_errors.push('account_type');
    if (entries.length === 0) new_errors.push('entries');
    entries.forEach((entry, i) => {
      if (entry.name === '') new_errors.push(`name_${i}`);
      if (!entry.iban_ok || entry.iban === '') new_errors.push(`iban_${i}`);
      if (isNaN(parseAmount(entry.amount)) || parseAmount(entry.amount) <= 0) new_errors.push(`amount_${i}`);
      if (entry.note.trim() === '') new_errors.push(`note_${i}`);
    });
    setErrors(new_errors);

    if (new_errors.length === 0 && !saving) {
      setSaving(true);

      const [first, ...rest] = entries;
      fetch_one('/nl/v3/objects/save', {
        object: 'pain_row',
        handler_id: login?.id,
        ob: {
          ...(!!editId ? {id: parseInt(editId, 10)} : {}),
          account_type: accountType,
          amount: parseAmount(first.amount),
          name: first.name,
          iban: normalizeIban(first.iban),
          note: first.note,
          user_id: user?.id,
          save_iban_to_user: first.save_iban_to_user ? 1 : 0
        },
        ...(rest.length > 0 ? {rows: rest.map((entry) => ({
          account_type: accountType,
          amount: parseAmount(entry.amount),
          name: entry.name,
          iban: normalizeIban(entry.iban),
          note: entry.note,
          user_id: user?.id,
          save_iban_to_user: entry.save_iban_to_user ? 1 : 0
        }))} : {})
      }, (ob, complete_data) => {
        saveNote();
      }, (z) => {
        setSaving(false);
        setServerErrorSnackOpen(true);
      }, login);
    }
  };

  const renderEntry = (entry: PainEntry, i: number) => (
    <Grid item xs={12} key={i}>
      <Box sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2
      }}>
        {(entries.length > 1 || (accountType === 'dividend' && !editId)) && <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2}}>
          <Typography variant="h2" sx={{fontSize: '1rem'}}>Entry {i + 1}</Typography>
          <StyledIconButton
            title="Remove entry"
            onClick={() => {
              setErrors([]);
              setEntries(entries.filter((_, j) => j !== i));
            }}
          >
            <DeleteIcon />
          </StyledIconButton>
        </Box>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StyledTextField
              id={`name_${i}`}
              label="Name"
              value={entry.name}
              fullWidth
              errorArray={errors}
              onChange={(v) => {updateEntry(i, {name: v});}}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              id={`amount_${i}`}
              label="Amount (EUR)"
              value={entry.amount}
              fullWidth
              errorArray={errors}
              onChange={(v) => {updateEntry(i, {amount: v});}}
            />
          </Grid>

          <Grid item xs={12}>
            {entry.country === 'BE' && <BankAccount
              id={`iban_${i}`}
              label="IBAN (BE)"
              value={entry.iban}
              fullWidth
              errorArray={errors}
              onChange={(v, checked) => {updateEntry(i, {iban: v, iban_ok: checked});}}
            />}
            {entry.country === 'NL' && <BankAccountDutch
              id={`iban_${i}`}
              label="IBAN (NL)"
              value={entry.iban}
              fullWidth
              errorArray={errors}
              onChange={(v, checked) => {updateEntry(i, {iban: v, iban_ok: checked});}}
            />}
            <ButtonGroup variant="outlined" size="small" sx={{marginTop: '-6px'}}>
              <Button
                id={`country_be_${i}`}
                onClick={() => {switchCountry(i, 'BE');}}
                variant={entry.country === 'BE' ? 'contained' : 'outlined'}
              >BE</Button>
              <Button
                id={`country_nl_${i}`}
                onClick={() => {switchCountry(i, 'NL');}}
                variant={entry.country === 'NL' ? 'contained' : 'outlined'}
              >NL</Button>
            </ButtonGroup>
            {!!user && entry.iban_ok && normalizeIban(entry.iban) !== normalizeIban(user.zicht_account) && <Box sx={{width: '100%', marginTop: 1}}>
              <StyledSwitch
                label="IBAN differs from the one on file"
                offLabel="Keep on file"
                onLabel="Also save to vennoot"
                value={entry.save_iban_to_user}
                onChange={(v) => {updateEntry(i, {save_iban_to_user: !!v});}}
              />
            </Box>}
          </Grid>

          <Grid item xs={12}>
            <StyledTextField
              id={`note_${i}`}
              label="Note (bank communication)"
              value={entry.note}
              fullWidth
              multiline
              errorArray={errors}
              onChange={(v) => {updateEntry(i, {note: v});}}
            />
            {(accountType === 'exit' || accountType === 'dividend') && <Box sx={{marginTop: '-6px'}}>
              <ButtonGroup variant="outlined" size="small">
                <Button
                  id={`note_nl_${i}`}
                  onClick={() => {setEntryLocale(i, 'nl');}}
                  variant={entryNoteLocale(entry) === 'nl' ? 'contained' : 'outlined'}
                >NL</Button>
                <Button
                  id={`note_fr_${i}`}
                  onClick={() => {setEntryLocale(i, 'fr');}}
                  variant={entryNoteLocale(entry) === 'fr' ? 'contained' : 'outlined'}
                >FR</Button>
              </ButtonGroup>
            </Box>}
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );

  const entryNoteLocale = (entry: PainEntry): string => {
    if (accountType === 'exit') return noteLocale;
    return /^Argen-Co dividende /.test(entry.note) ? 'fr' : 'nl';
  };

  return (
    <Page title={!!editId ? `Edit PAIN row #${editId}` : 'New PAIN row'}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!editId && <StyledButton
            label={showSearch ? 'Hide vennoot search' : 'Show vennoot search'}
            id='toggle_user_search'
            text
            startIcon={showSearch ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            onClick={() => {
              setShowSearch(!showSearch);
            }}
          />}
        </Grid>

        {!editId && !!showSearch && <Grid item xs={12}>
          <Typography variant="h2" sx={{fontSize: '1.1rem', marginBottom: 1}}>Find vennoot</Typography>
          <UserSearch
            skipApiRestrictions
            customUserLayout={["padding", "name", "vennr", "zicht_account", "padding", "padding", "padding", "open"]}
            fields={['id', 'vennoot_number', 'zicht_account', 'first_name', 'last_name', 'street', 'number', 'zip', 'city', 'enabled', 'stock_number', 'full_name', 'is_dutch']}
            resultLayout={(u) => <Box sx={{
              padding: 1,
              cursor: 'pointer',
              border: '1px solid transparent',
              borderColor: 'primary.main',
              marginBottom: 0.5,
              ...((!u.enabled || !((u.stock_number || 0) > 0)) ? {
                opacity: 0.4
              } : {})
            }} onClick={() => {
              pickUser(u);
            }}>
              <Typography sx={{fontWeight: 'bold'}}>
                {u.first_name} {u.last_name}
                <span style={{fontWeight: 'normal', float: 'right'}}>{u.zicht_account}</span>
              </Typography>
              <Typography>{u.street} {u.number}, {u.zip} {u.city}</Typography>
              <Typography>{u.vennoot_number} — {u.stock_number ? u.stock_number : 0} shares</Typography>
            </Box>}
          />
        </Grid>}

        <Grid item xs={12}>
          <Divider sx={{marginY: 2}} />
          <StyledSelect
            id="account_type"
            label="Account"
            value={accountType}
            fullWidth
            errorArray={errors}
            disabled={!!editId}
            onChange={(v) => {setAccountType(v);}}
            list={PAIN_ACCOUNTS.map((account) => ({id: account.name.toLowerCase(), name: `${account.name} (${account.description})`}))}
          />
        </Grid>

        {!!user && <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <PersonSearchIcon sx={{marginRight: 1}} />
            <Box sx={{flexGrow: 1}}>
              <Typography sx={{fontWeight: 'bold'}}>
                {user.full_name}
                {accountType === 'exit' && <ContentCopyIcon
                  titleAccess="Copy full name"
                  sx={{cursor: 'pointer', scale: '0.7', marginLeft: 1, marginBottom: -0.5, color: theme.palette.primary_grey?.main}}
                  onClick={(e) => {
                    e.preventDefault();
                    navigator.clipboard.writeText(user.full_name || '');
                    setNameCopiedSnackOpen(true);
                  }}
                />}
              </Typography>
              <Typography sx={{fontSize: '0.8rem'}}>{user.vennoot_number}</Typography>
            </Box>
            {!editId && <StyledIconButton title="Unlink vennoot" onClick={() => {
              setUser(undefined);
              setShowSearch(true);
              setEntries([emptyEntry()]);
              setGeneralNote('');
              setNoteLocale('nl');
              setErrors([]);
              setEntryEpoch(entryEpoch + 1);
            }}>
              <PersonOffIcon />
            </StyledIconButton>}
          </Box>
        </Grid>}

        {!editId && exitLoading && <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <CircularProgress size={20} sx={{marginRight: 2}} />
            <Typography>Checking for the last approved exit...</Typography>
          </Box>
        </Grid>}

        {!editId && !!exitInfo && <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: 'pointer'
          }} onClick={() => {
            updateEntry(entries.length - 1, {amount: exitInfo.amount.toFixed(2)});
          }}>
            <HistoryIcon sx={{marginRight: 1}} />
            <Typography>Last approved exit {exitInfo.given_id} ({exitInfo.label}): {formatEur(exitInfo.amount)} EUR</Typography>
            <Typography sx={{marginLeft: 'auto', fontSize: '0.8rem', color: theme.palette.primary?.main}}>Use this amount</Typography>
          </Box>
        </Grid>}

        {!editId && accountType === 'dividend' && !!user && <Grid item xs={12}>
          {dividendLoading && <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <CircularProgress size={20} sx={{marginRight: 2}} />
            <Typography>Fetching the last dividends...</Typography>
          </Box>}

          {!dividendLoading && dividends.length === 0 && <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <PaymentsIcon sx={{marginRight: 1}} />
            <Typography>No dividend calculations found for this vennoot, fill in manually below</Typography>
          </Box>}

          {!dividendLoading && dividends.length > 0 && <>
            <Typography variant="h2" sx={{fontSize: '1.1rem', marginBottom: 1}}>Last dividends</Typography>
            <Typography sx={{fontSize: '0.8rem', fontStyle: 'italic', color: theme.palette.primary_grey?.main, marginBottom: 1}}>
              Click a dividend to add it as a row. The prefilled entries can be edited, but normally shouldn't be.
              Checkmarks indicate the dividend already appears in a pain file (possibly already paid, unless the payment bounced).
            </Typography>
            <Grid container spacing={1} sx={{marginTop: 0}}>
              {dividends.map((d) => {
                const used = entries.some((e) => e.dividend_calculation_id === d.id);
                return <Grid item xs={6} md={2} key={d.id} sx={{display: 'flex'}}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    width: '100%',
                    boxSizing: 'border-box',
                    ...((used ? {opacity: 0.4, cursor: 'default'} : {}))
                  }} onClick={() => {if (!used) addDividendEntry(d);}}>
                    {d.in_pain_file
                      ? <CheckIcon sx={{marginBottom: 0.5, color: theme.palette.tertiary?.main}} />
                      : <PaymentsIcon sx={{marginBottom: 0.5}} />}
                    <Typography sx={{fontWeight: 'bold'}}>{d.book_year}</Typography>
                    <Typography>{d.amount !== undefined ? formatEur(d.amount) : '?'} EUR</Typography>
                    <Typography sx={{fontSize: '0.8rem', color: d.in_pain_file ? theme.palette.tertiary?.main : theme.palette.primary?.main}}>
                      {d.in_pain_file ? 'In previous pain file' : used ? 'Added' : 'Add row'}
                    </Typography>
                    {d.in_pain_file && <Typography sx={{fontSize: '0.8rem', color: theme.palette.primary?.main}}>{used ? 'Added' : 'Add row'}</Typography>}
                  </Box>
                </Grid>;
              })}
            </Grid>
          </>}
        </Grid>}

        {entries.length > 1 && <Grid item xs={12}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
            <Typography sx={{fontWeight: 'bold'}}>Total: {formatEur(totalAmount())} EUR</Typography>
            {!!exitInfo && <>
              {totalsMatch()
                ? <CheckIcon sx={{color: theme.palette.tertiary?.main}} />
                : <CloseIcon sx={{color: theme.palette.secondary.main}} />}
              <Typography sx={{fontSize: '0.8rem', color: theme.palette.primary_grey?.main}}>(expected {formatEur(exitInfo.amount)} EUR)</Typography>
            </>}
          </Box>
        </Grid>}

        {entries.map((entry, i) => <React.Fragment key={`${i}-${entryEpoch}`}>
          {i === 0 && accountType === 'dividend' && !editId && <Grid item xs={12} sx={{paddingTop: '16px !important'}} />}
          {renderEntry(entry, i)}
        </React.Fragment>)}

        {accountType === 'exit' && <Grid item xs={12}>
          <StyledButton
            label="Add extra recipient"
            id='add_entry'
            text
            startIcon={<AddCircleOutlineIcon />}
            disabled={!!editId}
            onClick={() => {
              setErrors([]);
              const entry = emptyEntry();
              if (!!user?.vennoot_number) entry.note = exitRef(noteLocale, user.vennoot_number);
              setEntries([...entries, entry]);
            }}
          />
          {!!editId && <Typography sx={{fontSize: '0.8rem', fontStyle: 'italic', color: theme.palette.primary_grey?.main}}>
            Only available when creating a PAIN row
          </Typography>}
        </Grid>}

        {!!user && <Grid item xs={12}>
          <Divider sx={{marginY: 2}} />
          <Typography variant="h2" sx={{fontSize: '1.1rem', marginBottom: 1}}>Note for vennoot</Typography>
          <StyledTextField
            id="general_note"
            label="General note (added to the vennoot's notes)"
            value={generalNote}
            fullWidth
            multiline
            onChange={(v) => {setGeneralNote(v);}}
          />
        </Grid>}

        <Grid item xs={12} sx={{alignContent: 'baseline', textAlign: 'center', position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1201}}>
          <Divider sx={{marginTop: 1, marginBottom: 2}} />
          <StyledButton
            label={saving ? 'Saving...' : 'Save'}
            id='save'
            contained
            saveStartIcon
            disabled={!canSave()}
            onClick={() => {
              doSave();
            }}
          />
          <StyledButton
            label="Cancel"
            id='cancel'
            disabled={saving}
            onClick={() => {
              navigate(!!editId && !!accountType ? `/create_pain/rows/${accountType}` : '/create_pain');
            }}
            sx={{marginLeft: 1}}
          />
        </Grid>
      </Grid>

      {!!savedSnackOpen && <AlertSaved open={savedSnackOpen} setOpen={(b) => {setSavedSnackOpen(b);}} />}
      {!!nameCopiedSnackOpen && <Snackbar
        open={nameCopiedSnackOpen}
        onClose={() => {setNameCopiedSnackOpen(false);}}
        autoHideDuration={2000}
      >
        <MuiAlert elevation={6} variant="filled" severity="success">Copied!</MuiAlert>
      </Snackbar>}
      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
    </Page>
  );
}
